import React, { useMemo } from 'react';
import useI18n from 'i18n/useI18n';
import styled from 'styled-components';
import { Input } from '@etiquette-ui/inputs-dropdowns';
import Switches from 'pages/MenuItems/components/Switches';
import DaysSelector from 'pages/MenuItems/components/DaysSelector';
import SecondaryButton from 'pages/MenuItems/components/SecondaryButton';
import TimeRangeSelector from 'pages/MenuItems/components/TimeRangeSelector';
import { PRICING_STRATEGIES } from 'pages/MenuItems/pages/Items/resources/constants';

const pricingStrategy = PRICING_STRATEGIES.timeBasedPrice;

const Title = styled.h1`
  font-size: 16px;
  font-weight: 400;
  margin-bottom: 5px;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  
  &._button {
    margin-top: 10px;
    @media( max-width: 768px ) {
      justify-content: center;
    }
  }
`;
const RuleContainer = styled.div`
  flex: 0 0 100%;
  max-width: 100%;

  gap: 5px;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;

  .days-selector {
    max-width: 330px;
    width: fit-content;
    margin-bottom: 5px;
  }

  .price {
    margin-right: 5px;
    margin-bottom: 5px;
    max-width: 50%;
  }

  @media ( min-width: 768px ) {
    flex: 80%;
    max-width: 80%;

    .price {
      max-width: 130px;
    }
  }
`;

const SwitchesContainer = styled.div`
  flex: 0 0 100%;
  max-width: 100%;
  padding-top: 10px;
  @media ( min-width: 768px ) {
    flex: 20%;
    max-width: 20%;
    padding-bottom: 2px;
  }
`;

const Rule = styled.div`
  padding-bottom: 10px;  
`;

const KEYS = {
  START: 'start',
  END: 'end',
  DAYS: 'days',
  PRICE: 'timeSpecificPrice',
  IS_ACTIVE: 'isActive',
  TIME_RANGE: 'timeRange',
}

const INIT_VARIANT = {
  [KEYS.DAYS]: [],
  [KEYS.PRICE]: 0,
  [KEYS.IS_ACTIVE]: true,
  [KEYS.TIME_RANGE]: {    
    [KEYS.START]: null,
    [KEYS.END]: null,
  },
}

const TimeBasedStrategy = ({variant, onChange}) => {
  const { translate } = useI18n();

  const rules = useMemo(()=>{
    if(!variant.config || !variant.config.pricingStrategies ||
      !variant.config.pricingStrategies[pricingStrategy]) 
        return new Map([[`${new Date()}`, {...INIT_VARIANT}]]);
    return variant.config.pricingStrategies[pricingStrategy];
  },[variant]);

  const onEdit = (idx, key, value) => {
    let newRules = rules;
    let rule = newRules.get(idx);
    
    newRules.set(idx, {
      ...rule,
      [key]: value
    })
    updateVariant(newRules);
  }

  const onDelete = (idx) => {
    let newRules = rules;
    newRules.delete(idx);    
    updateVariant(newRules);
  }

  const onCreate = () => {
    let newRules = rules;

    if(!newRules) newRules = new Map();
    
    newRules.set(
      `${new Date()}`,
      INIT_VARIANT);
    updateVariant(newRules);
  }

  const updateVariant = ( rules ) => {
    let newVariant = {...variant};

    if(!newVariant.config || !newVariant.config.pricingStrategies){
      newVariant.config = {
        ...newVariant.config,
        pricingStrategies: {
          [pricingStrategy]: null
        }
      }
    }
    
    newVariant.config.pricingStrategies[pricingStrategy] = rules;
    onChange(newVariant);
  }

  return (
    <div>
      {!!rules && rules.size > 0 && Array.from(rules.entries()).map( ([idx, rule]) => (
        <Rule>
          <Title>
            {translate('Time-Based Price Rule')}
          </Title>
          <Row style={{alignItems: 'end'}}>
            <RuleContainer>
              <TimeRangeSelector
                startProps={{
                  value: rule[KEYS.TIME_RANGE][KEYS.START],
                  onChange: (value)=>onEdit(
                    idx,
                    KEYS.TIME_RANGE, {
                    [KEYS.START]: value, 
                    [KEYS.END]: rule[KEYS.TIME_RANGE][KEYS.END]
                  }),
                }}
                endProps={{
                  value: rule[KEYS.TIME_RANGE][KEYS.END],
                  onChange: (value)=>onEdit(
                    idx,
                    KEYS.TIME_RANGE, {
                    [KEYS.START]: rule[KEYS.TIME_RANGE][KEYS.START], 
                    [KEYS.END]: value
                  }),
                }}
              />
              <Input
                labelProps={{ label: translate("Price") }}
                iconProps={{ icon: "price" }}
                inputProps={{
                  value: rule[KEYS.PRICE],
                  onChange: (e) => onEdit(idx, KEYS.PRICE, e.target.value),
                  type: "number",
                  required: true,
                }}
                parentContainerClassName="price"
              />
              <DaysSelector
                selectedDays={rule[KEYS.DAYS]}
                containerClassName={'days-selector'}
                onChange={ (days) => onEdit(idx, KEYS.DAYS, days) }
              />
            </RuleContainer>
            <SwitchesContainer>
              <Switches
                active={rule[KEYS.IS_ACTIVE]}
                onDisable={() => onEdit(idx, KEYS.IS_ACTIVE, !rule.isActive)}
                onDelete={()=>onDelete(idx)}
                includeDeleteOption={rules.size > 1}
              />
            </SwitchesContainer>
          </Row>
        </Rule>
      ))}
      <Row className='_button'>
        <SecondaryButton
          label={translate('Add New Rule')}
          onClick={()=>onCreate()}
        />
      </Row>
    </div>
  )
};
export default TimeBasedStrategy;